import React from 'react';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const REQ_CLASS = 'required-field';

// Mirrors the red-left-border treatment applied to required TextInputs so
// required Selects (e.g. Gender on the family-history questionnaire) read
// visually as required at a glance — same selector pattern as TextInput.
const StyledFormControl = styled(FormControl)({
  [`&.${REQ_CLASS} .MuiOutlinedInput-notchedOutline`]: {
    borderLeft: '3px solid red'
  },
  [`&.${REQ_CLASS} .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
    &.${REQ_CLASS} .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline`]:
    {
      borderLeft: '3px solid red'
    }
});

export default function SelectInput(props) {
  const {
    value,
    name,
    onChange,
    options,
    variant,
    fullWidth,
    showPlaceholder,
    disabled,
    className,
    validated,
    helperText,
    required,
    // Opt-in: make the placeholder option selectable again, so a value can be
    // cleared. It is disabled by default because most selects here are a
    // required choice, where re-picking "Select" is never what the user wants.
    allowEmpty
  } = props;
  const error = (validated && !value) || props?.error;

  const t = useAutoTranslation();

  const combinedClassName =
    [className, required ? REQ_CLASS : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <StyledFormControl
      variant={variant ?? undefined}
      fullWidth={fullWidth ?? undefined}
      disabled={disabled ?? undefined}
      className={combinedClassName}
      error={props.highlightErrorBorder || error}
      size={props.size || 'medium'}
    >
      <Select
        native
        placeholder={showPlaceholder && 'Select'}
        value={value}
        onChange={onChange}
        inputProps={{
          name: name,
          id: name
        }}
      >
        {!props.hidePlaceholder && (
          <option value="" disabled={!allowEmpty}>
            {t(` Select`)}
          </option>
        )}
        {options?.map((el, i) => (
          <option value={el.value} key={`${props.name}-${i}`}>
            {t(el.label)}
          </option>
        ))}
      </Select>
      {error && (
        <FormHelperText>
          {t(helperText) || t(props?.error) || t('Please select a valid field')}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
}
